import { initializeAuth, getAuth } from 'firebase/auth';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from "firebase/functions";

// Initialize Firebase
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize app
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const FIREBASE_AUTH = getAuth(app);
export const FIRESTORE_DB = getFirestore(app);
export const FIREBASE_STORAGE = getStorage(app);
export const FIREBASE_FUNCTIONS = getFunctions(app, 'europe-west1');

// Firebase Functions callable exports
export const saveContactsEurW = httpsCallable(FIREBASE_FUNCTIONS, 'saveContactsEurW');
export const addPersonFromUserEurW = httpsCallable(FIREBASE_FUNCTIONS, 'addPersonFromUserEurW');
export const draftMessageEurW = httpsCallable(FIREBASE_FUNCTIONS, 'draftMessageEurW');
export const getDraftQuotaEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getDraftQuotaEurW');
// PPL-350 — f8's memory limits: how many ppl it remembers, and how far back.
export const getRememberedPeopleQuotaEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getRememberedPeopleQuotaEurW');
export const setPersonRememberedEurW = httpsCallable(FIREBASE_FUNCTIONS, 'setPersonRememberedEurW');
export const getMemoryArchiveEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getMemoryArchiveEurW');
// PPL-358 — the day-14 takeover: the receipt's numbers + the one-commit choice.
export const getTrialSummaryEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getTrialSummaryEurW');
export const commitRememberedSelectionEurW = httpsCallable(FIREBASE_FUNCTIONS, 'commitRememberedSelectionEurW');
export const getVoiceChatQuotaEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getVoiceChatQuotaEurW');
export const translateMessageEurW = httpsCallable(FIREBASE_FUNCTIONS, 'translateMessageEurW');
export const deleteUserEurW = httpsCallable(FIREBASE_FUNCTIONS, 'deleteUserEurW');
export const timeZoneConversionEurWV2 = httpsCallable(FIREBASE_FUNCTIONS, 'timeZoneConversionEurWV2');
export const greetingsMessageEurW = httpsCallable(FIREBASE_FUNCTIONS, 'greetingsMessageEurW');
export const greetingsResponseEurW = httpsCallable(FIREBASE_FUNCTIONS, 'greetingsResponseEurW');
export const relationshipTipsEurW = httpsCallable(FIREBASE_FUNCTIONS, 'relationshipTipsEurW');
export const chatEurW = httpsCallable(FIREBASE_FUNCTIONS, 'chatEurW');
export const chatThreadManagement = httpsCallable(FIREBASE_FUNCTIONS, 'chatThreadManagement');
export const onboardingConversationStartersEurW = httpsCallable(FIREBASE_FUNCTIONS, 'onboardingConversationStartersEurW');
export const getPplTalkMessagesEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getPplTalkMessagesEurW');
export const getPplTalkMessageEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getPplTalkMessageEurW');
export const adminDashboardDataEurW = httpsCallable(FIREBASE_FUNCTIONS, 'adminDashboardDataEurW');
export const manageEmailSubscriptionEurW = httpsCallable(FIREBASE_FUNCTIONS, 'manageEmailSubscriptionEurW');
export const getPplCurrentlyOnline = httpsCallable(FIREBASE_FUNCTIONS, 'getPplCurrentlyOnline');
export const chatTopicSuggestionsEurW = httpsCallable(FIREBASE_FUNCTIONS, 'chatTopicSuggestionsEurW');

// PayFast web subscriptions
export const createWebCheckoutEurW = httpsCallable(FIREBASE_FUNCTIONS, 'createWebCheckoutEurW');
export const cancelWebSubscriptionEurW = httpsCallable(FIREBASE_FUNCTIONS, 'cancelWebSubscriptionEurW');
export const confirmWebPaymentEurW = httpsCallable(FIREBASE_FUNCTIONS, 'confirmWebPaymentEurW');

// Support contact (email served from backend, kept out of the static bundle)
export const getSupportEmailEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getSupportEmailEurW');

// PPL-265 — write-back review queue (accept/edit/reject + undo). Reads are
// direct Firestore subscriptions (owner-readable); every WRITE goes through
// these callables because the suggestion/timeline subtrees are Admin-only.
export const respondToSuggestionEurW = httpsCallable(FIREBASE_FUNCTIONS, 'respondToSuggestionEurW');
export const undoWritebackItemEurW = httpsCallable(FIREBASE_FUNCTIONS, 'undoWritebackItemEurW');

// PPL-328/329 — user-authored timeline writes (log a hangout, content edits).
export const logHangoutEurW = httpsCallable(FIREBASE_FUNCTIONS, 'logHangoutEurW');
export const editTimelineEntryEurW = httpsCallable(FIREBASE_FUNCTIONS, 'editTimelineEntryEurW');

// PPL-284/464 — channel links (whatsapp). The code is minted inside a
// signed-in session and the USER sends it to the bot; the connected state is
// read live from the owner-readable `users/{uid}/channelLinks` projection.
export const createChannelLinkCodeEurW = httpsCallable(FIREBASE_FUNCTIONS, 'createChannelLinkCodeEurW');
export const revokeChannelLinkEurW = httpsCallable(FIREBASE_FUNCTIONS, 'revokeChannelLinkEurW');

// PPL-416 — the home desk. One feed for the /f8chat home state, a dismiss /
// undo with module-specific windows, and the cold opener (called only when
// the feed arrives with `opener: null`, always with the feed's stage + modules).
export const homeFeedEurW = httpsCallable(FIREBASE_FUNCTIONS, 'homeFeedEurW');
export const homeDismissEurW = httpsCallable(FIREBASE_FUNCTIONS, 'homeDismissEurW');
export const homeOpenerEurW = httpsCallable(FIREBASE_FUNCTIONS, 'homeOpenerEurW');
